import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A modal sheet: slides up from the bottom on a phone (grabber, swipe down to
 * dismiss), centred on desktop. Escape, the backdrop and the close button
 * dismiss it; focus moves to the first control inside and returns to where it
 * was on close. Used for every menu, picker and form so a phone never needs a
 * hover state. `action` is the iOS top-right primary ("Save", "Add").
 */
export default function Sheet({
  title,
  onClose,
  action,
  className,
  children,
}: {
  title: string;
  onClose: () => void;
  action?: { label: string; onClick: () => void; disabled?: boolean };
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const body = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [drag, setDrag] = useState(0);
  const start = useRef<{ y: number; id: number } | null>(null);

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first =
      body.current?.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ??
      body.current?.querySelector<HTMLElement>('button, [href], input, select, textarea');
    first?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      before?.focus();
    };
  }, []);

  // Swipe down on the grabber/header to dismiss (touch only).
  const grab = {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      start.current = { y: e.clientY, id: e.pointerId };
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (start.current?.id === e.pointerId) setDrag(Math.max(0, e.clientY - start.current.y));
    },
    onPointerUp: () => {
      start.current = null;
      if (drag > 90) close.current();
      else setDrag(0);
    },
    onPointerCancel: () => {
      start.current = null;
      setDrag(0);
    },
  };

  return (
    <div
      className="lo-sheet-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`lo-sheet${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
      >
        <div className="lo-sheet__grab" aria-hidden="true" {...grab}>
          <span />
        </div>
        <div className="lo-sheet__head" {...grab}>
          <h2 className="lo-sheet__title">{title}</h2>
          {action && (
            <button type="button" className="lo-sheet__action" onClick={action.onClick} disabled={action.disabled}>
              {action.label}
            </button>
          )}
          <button type="button" className="lo-sheet__close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="lo-sheet__body" ref={body}>
          {children}
        </div>
      </div>
    </div>
  );
}
